// frontend/src/services/translation/translationService.js
import api from '../api';
import errorService from '../error/errorService';
import cacheService from '../cache/cacheService';
import creditService from '../credit/creditService';

/**
 * Translation Service - Handles all translation-related operations
 */
class TranslationService {
  /**
   * Translate a batch of text
   * @param {string} batchId - The ID of the batch to translate
   * @param {Array} glossary - Optional glossary terms
   * @returns {Promise} - Promise that resolves with the translation result
   */
  async translateBatch(batchId, glossary = null) {
    try {
      // Create request payload
      const payload = glossary ? { glossary } : {};
      
      // Call the API
      const response = await api.post(`/batches/${batchId}/translate`, payload);
      
      // Update the credit balance after translation
      if (response.data && response.data.new_balance !== undefined) {
        await creditService.updateCreditBalance();
      }
      
      return response.data;
    } catch (error) {
      // Map error responses to appropriate types
      if (error.response) {
        switch (error.response.status) {
          case 402: // Payment Required
            return { 
              error: true, 
              type: 'INSUFFICIENT_CREDITS',
              message: error.response.data?.detail || 'Insufficient credits',
              detail: error.response.data?.detail
            };
          case 503: // Service Unavailable
            return { 
              error: true, 
              type: 'SERVICE_OVERLOADED',
              message: 'Translation service is currently overloaded'
            };
          default:
            return errorService.handleApiError(error, 'Failed to translate batch');
        }
      }
      
      return errorService.handleNetworkError(error, 'Translation failed due to network error');
    }
  }

  /**
   * Regenerate a translation for a batch
   * @param {string} batchId - The ID of the batch
   * @param {Array} glossary - Optional glossary terms
   * @returns {Promise} - Promise that resolves with the regeneration result
   */
  async regenerateTranslation(batchId, glossary = null) {
    try {
      // Create request payload
      const payload = glossary ? { glossary } : {};
      
      // Call the API
      const response = await api.post(`/batches/${batchId}/regenerate`, payload);
      
      // Update the credit balance after regeneration
      if (response.data && response.data.new_balance !== undefined) {
        await creditService.updateCreditBalance();
      }
      
      return response.data;
    } catch (error) {
      // Map error responses to appropriate types
      if (error.response) {
        switch (error.response.status) {
          case 402: // Payment Required
            return { 
              error: true, 
              type: 'INSUFFICIENT_CREDITS',
              message: error.response.data?.detail || 'Insufficient credits',
              detail: error.response.data?.detail
            };
          case 503: // Service Unavailable
            return { 
              error: true, 
              type: 'SERVICE_OVERLOADED',
              message: 'Translation service is currently overloaded'
            };
          default:
            return errorService.handleApiError(error, 'Failed to regenerate translation');
        }
      }
      
      return errorService.handleNetworkError(error, 'Regeneration failed due to network error');
    }
  }

  /**
   * Save edited translation text
   * @param {string} batchId - The ID of the batch
   * @param {string} translatedText - The edited translation
   * @param {string} status - Optional status update
   * @returns {Promise} - Promise that resolves when save completes
   */
  async saveTranslation(batchId, translatedText, status = null) {
    try {
      // Create payload with all necessary fields
      const payload = { 
        translated_text: translatedText
      };
      
      // Add status if provided
      if (status) {
        payload.status = status;
      }
      
      // First try to save using the main endpoint
      const response = await api.post(`/batches/${batchId}/save`, payload);
      return response.data;
    } catch (firstError) {
      console.warn('First save attempt failed, trying alternative method', firstError);
      
      try {
        // Create a unique request ID for retry
        const requestId = `retry-${Date.now()}-${Math.random().toString(36).substring(2)}`;
        
        // Retry with additional parameters
        const response = await api.post(
          `/batches/${batchId}/save?retry=true&rid=${requestId}`,
          {
            translated_text: translatedText,
            status: status || "edited"
          },
          {
            headers: {
              'X-Request-ID': requestId,
              'X-Operation-Type': 'autosave_retry'
            },
            timeout: 20000 // Longer timeout for retry
          }
        );
        return response.data;
      } catch (retryError) {
        return errorService.handleApiError(retryError, 'Failed to save translation after retry');
      }
    }
  }

  /**
   * Download the complete translated document
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise that resolves with the document content
   */
  async downloadTranslatedDocument(documentId) {
    try {
      const response = await api.get(`/documents/${documentId}/download`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to download translated document');
    }
  }
}

export default new TranslationService();